import {
  TypeOfAnsokan,
  OpenWebAnsokanInfoBubbles,
  SkyddadYrkestitelOpenFormInfoBubbles,
  FormType,
  OutSideEuEesType,
} from '../PublicForm.types';
import { Yrke } from '../../AnsokanForms/LegAnsokanForm.types';
import { DocumentModel, Document, DOCUMENT_LIST, DOCUMENT_LIST_SKYDDAD_YRKESTITEL } from './document.model.types';
import _ from 'lodash';

type TranslateType = (key: string, defaultValue?: string) => string;
export interface GetDocumentModelParams {
  formType: TypeOfAnsokan;
  yrke: Yrke;
  worked3yearsInEU: boolean;
  isSpecialistsjukskoterska: boolean;
  hasSpecialistkompetens: boolean;
  hasDoubleSpecialistkompetens: boolean;
}

export interface GetSkyddadYrkestitelDocumentModelParams {
  yrke: Yrke;
  educatedInSweden: boolean;
}

export class DocumentFetcher {
  private Translate: TranslateType;
  private isSkyddadYrkestitelForm: boolean;
  private openWebAnsokanInfoBubbles?: OpenWebAnsokanInfoBubbles;
  private skyddadYrkestitelOpenFormInfoBubbles?: SkyddadYrkestitelOpenFormInfoBubbles;
  private DocumentMap: Map<Document, DocumentModel>;
  private outSideEuEesType: OutSideEuEesType = OutSideEuEesType.None;
  private outSideEuEesYrke: Yrke = null;

  constructor(
    Translate: TranslateType,
    isSkyddadYrkestitelForm?: boolean,
    openWebAnsokanInfoBubbles?: OpenWebAnsokanInfoBubbles,
    skyddadYrkestitelOpenFormInfoBubbles?: SkyddadYrkestitelOpenFormInfoBubbles,
  ) {
    this.Translate = Translate;
    this.isSkyddadYrkestitelForm = isSkyddadYrkestitelForm;

    if (isSkyddadYrkestitelForm) {
      this.skyddadYrkestitelOpenFormInfoBubbles = skyddadYrkestitelOpenFormInfoBubbles;
    } else {
      this.openWebAnsokanInfoBubbles = openWebAnsokanInfoBubbles;
    }

    this.DocumentMap = this.createDocumentMap();
  }

  public getOutsideEuEesModels = (type: OutSideEuEesType, yrke: Yrke): DocumentModel[] => {
    // Remember the flow so every later getModels() call short-circuits and never re-runs EU logic.
    this.outSideEuEesType = type;
    this.outSideEuEesYrke = yrke;

    const models: DocumentModel[] = [];
    const add = (document: Document, isRequired: boolean) => {
      const model = _.cloneDeep(this.DocumentMap.get(document));
      if (!model) return;
      if (type === OutSideEuEesType.OutsideEuEes && document === Document.Sprakintyg) {
        model.title = this.Translate('doc_sprakintygutanforeu');
      }
      model.isRequired = isRequired;
      model.order = models.length;
      models.push(model);
    };

    if (type === OutSideEuEesType.Assessment) {
      add(Document.PersonbevisEllerPass, true);
      add(Document.KvittensForBetalning, true);
      add(Document.Examensbevis, false);
      add(Document.Kursbeskrivning, false);
      add(Document.IntygNamnbyte, false);
      add(Document.Ovrigt, false);

      if (yrke === Yrke.Psykolog || yrke === Yrke.Psykoterapeut || yrke === Yrke.Kiropraktor) {
        add(Document.Kursplan, false);
        add(Document.DiplomaSupplement, false);
      }
      if (yrke === Yrke.Sjukskoterska) {
        add(Document.Gymnasiebetyg, false);
      }
    } else if (type === OutSideEuEesType.OutsideEuEes) {
      add(Document.PersonbevisEllerPass, true);
      add(Document.Sprakintyg, false);
      add(Document.KvittensForBetalning, false);
      add(Document.PraktiskTjanstgoring, false);
      add(Document.SvenskaForfattningar, false);
    }

    // Hard guarantee: no EU-directive (Artikel_*) document can ever leak into these flows.
    return models.filter((model) => !model.fieldName.startsWith('doc_artikel'));
  };

  public getSkyddadYrkestitelModels = (params: GetSkyddadYrkestitelDocumentModelParams): DocumentModel[] => {
    const { yrke, educatedInSweden } = params;

    if (!yrke) return null;

    const models = this.getBaseDocuments(null, educatedInSweden);

    this.setIsRequired(models, false, yrke, educatedInSweden);

    // Returns a sorted array of objects (sorted by order)
    return Array.from(new Map(models).values()).sort((a, b) => {
      return a.order - b.order;
    });
  };

  public getModels = (params: GetDocumentModelParams): DocumentModel[] => {
    // STRICT SHORT-CIRCUIT: Outside EU/EES and Assessment flows must never touch EU logic
    // (getBaseDocuments / getSpecificYrkeDocuments / getSpecialistDocuments / Artikel_* rules).
    if (
      this.outSideEuEesType === OutSideEuEesType.Assessment ||
      this.outSideEuEesType === OutSideEuEesType.OutsideEuEes
    ) {
      return this.getOutsideEuEesModels(this.outSideEuEesType, params.yrke ?? this.outSideEuEesYrke);
    }

    const {
      formType,
      yrke,
      hasDoubleSpecialistkompetens,
      hasSpecialistkompetens,
      isSpecialistsjukskoterska,
      worked3yearsInEU,
    } = params;
    // If yrke is not set, simply exit with null
    if (!yrke) return null;

    // First get a basic collection of documents (pass yrke for conditional base docs)
    const models = this.getBaseDocuments(formType, false, yrke);

    // Get documents for a specific yrke
    this.getSpecificYrkeDocuments(yrke, worked3yearsInEU, formType).forEach((document) => {
      models.set(document, _.cloneDeep(this.DocumentMap.get(document)));
    });

    // Get correct specialist documents
    this.getSpecialistDocuments(
      yrke,
      isSpecialistsjukskoterska,
      hasSpecialistkompetens,
      hasDoubleSpecialistkompetens,
      worked3yearsInEU,
    ).forEach((document) => {
      models.set(document, _.cloneDeep(this.DocumentMap.get(document)));
    });

    this.setIsRequired(models, worked3yearsInEU, yrke);
    if (hasSpecialistkompetens && (yrke === Yrke.Lakare || yrke === Yrke.Tandlakare)) {
      this.adjustDocumentOrder(models);
    }

    // Returns a sorted array of objects (sorted by order)
    return Array.from(new Map(models).values()).sort((a, b) => {
      return a.order - b.order;
    });
  };

  private setIsRequired = (
    map: Map<Document, DocumentModel>,
    worked3yearsInEU: boolean,
    yrke: Yrke,
    educatedInSweden = false,
  ) => {
    function getWithNullcheck(document: Document) {
      return map.get(document) || { isRequired: null };
    }

    if (this.isSkyddadYrkestitelForm) {
      getWithNullcheck(Document.KvittensForBetalning).isRequired = true;
      getWithNullcheck(Document.PersonbevisEllerPass).isRequired = true;
      getWithNullcheck(Document.DokumentFranUtbildning).isRequired = true;
      getWithNullcheck(Document.Kursbeskrivning).isRequired = true;
    } else {
      // These documents are always required
      getWithNullcheck(Document.KvittensForBetalning).isRequired = true;
      getWithNullcheck(Document.PersonbevisEllerPass).isRequired = true;
      getWithNullcheck(Document.Sprakintyg).isRequired = true;
      getWithNullcheck(Document.Examensbevis).isRequired = true;
      getWithNullcheck(Document.IntygAktuellBehorighet).isRequired = true;
      getWithNullcheck(Document.KopiaAndraSpecialistkompetensbevis).isRequired = true;
      getWithNullcheck(Document.KopiaForstaSpecialistkompetensbevis).isRequired = true;
      getWithNullcheck(Document.KopiaSpecialistkompetensbevis).isRequired = true;
      getWithNullcheck(Document.Artikel_25_28_allmanmedicin_23).isRequired = true;
      getWithNullcheck(Document.Artikel_25_28_allmanmedicin_23_forsta).isRequired = true;
      getWithNullcheck(Document.Artikel_25_28_allmanmedicin_23_andra).isRequired = true;
      getWithNullcheck(Document.Artikel_11_23).isRequired = true;
      getWithNullcheck(Document.Artikel_35_23).isRequired = true;

      if (yrke === Yrke.Sjukskoterska) getWithNullcheck(Document.KopiaSpecialistutbildning).isRequired = true;

      // All who use Artikel 11
      if (map.get(Document.Artikel_11)) {
        getWithNullcheck(Document.DokumentFranUtbildning).isRequired = true;
      }
      // Everyone else
      else {
        getWithNullcheck(Document.Artikel_24_23).isRequired = true;
        getWithNullcheck(Document.Artikel_31_33_33a_23).isRequired = true;
        getWithNullcheck(Document.Artikel_34_37_23).isRequired = true;
        getWithNullcheck(Document.Artikel_43_43a_40_41_23).isRequired = true;
        getWithNullcheck(Document.Artikel_44_23).isRequired = true;
        getWithNullcheck(Document.Artikel_3_dot_3).isRequired = true;

        // worked3yearsInEu always uses Artikel 3.3
        if (worked3yearsInEU) {
          // Sektorsyrken (these 5) should not require DokumentFranUtbildning.
          switch (yrke) {
            case Yrke.Apotekare:
            case Yrke.Barnmorska:
            case Yrke.Lakare:
            case Yrke.Sjukskoterska:
            case Yrke.Tandlakare:
              break;
            default:
              getWithNullcheck(Document.DokumentFranUtbildning).isRequired = true;
          }
        }
      }
    }
  };

  private getSpecialistDocuments = (
    yrke: Yrke,
    isSpecialistsjukskoterska: boolean,
    hasSpecialistkompetens: boolean,
    hasDoubleSpecialistkompetens: boolean,
    worked3yearsInEU: boolean,
  ) => {
    if (!(isSpecialistsjukskoterska || hasSpecialistkompetens)) return [];

    const isLakareEllerTandSpec = this.isSpecialistLakareOrTandlakare(yrke, hasSpecialistkompetens);
    const specialistDocuments: Document[] = [];

    if (hasDoubleSpecialistkompetens) {
      if (isLakareEllerTandSpec) {
        // Läkare/Tandläkare med dubbel specialist: endast kompetensbevis (inga utbildningsdokument)
        specialistDocuments.push(
          Document.KopiaForstaSpecialistkompetensbevis,
          Document.KopiaAndraSpecialistkompetensbevis,
        );
      } else {
        // Andra yrken: både utbildning och kompetensbevis för båda
        specialistDocuments.push(
          Document.KopiaForstaSpecialistutbildning,
          Document.KopiaForstaSpecialistkompetensbevis,
          Document.KopiaAndraSpecialistutbildning,
          Document.KopiaAndraSpecialistkompetensbevis,
        );
      }
    } else {
      if (isLakareEllerTandSpec) {
        specialistDocuments.push(Document.KopiaSpecialistkompetensbevis);
      } else {
        specialistDocuments.push(Document.KopiaSpecialistutbildning, Document.KopiaSpecialistkompetensbevis);
      }
    }

    if (isSpecialistsjukskoterska && !worked3yearsInEU) {
      specialistDocuments.push(Document.Tjanstgoringsintyg);
    }

    switch (yrke) {
      case Yrke.Lakare:
        if (!worked3yearsInEU) {
          if (!hasDoubleSpecialistkompetens) {
            specialistDocuments.push(Document.Artikel_25_28_allmanmedicin_23);
          } else {
            specialistDocuments.push(
              Document.Artikel_25_28_allmanmedicin_23_forsta,
              Document.Artikel_25_28_allmanmedicin_23_andra,
            );
          }
        }

        specialistDocuments.push(Document.IntygAktuellBehorighet);
        break;
      case Yrke.Sjukskoterska:
        // specialistDocuments.push(Document.Artikel_11_23);
        break;
      case Yrke.Tandlakare:
        specialistDocuments.push(Document.IntygAktuellBehorighet);
        if (!worked3yearsInEU) {
          specialistDocuments.push(Document.Artikel_35_23);
        }
        break;
    }
    return specialistDocuments;
  };

  private getSpecificYrkeDocuments = (yrke: Yrke, worked3yearsInEU: boolean, formType: TypeOfAnsokan): Document[] => {
    if (formType === TypeOfAnsokan.Specialistkompetens) {
      if (worked3yearsInEU) {
        switch (yrke) {
          case Yrke.Lakare:
          case Yrke.Sjukskoterska:
          case Yrke.Tandlakare:
            return [Document.Artikel_3_dot_3];
        }
      }
    }

    if (worked3yearsInEU) {
      switch (yrke) {
        case Yrke.Apotekare:
        case Yrke.Barnmorska:
        case Yrke.Lakare:
        case Yrke.Sjukskoterska:
        case Yrke.Tandlakare:
          return [Document.Artikel_3_dot_3];
        default:
          return [Document.Artikel_3_dot_3, Document.Tjanstgoringsintyg];
      }
    } else {
      switch (yrke) {
        case Yrke.Apotekare:
          return [Document.Artikel_44_23];
        case Yrke.Barnmorska:
          return [Document.Artikel_43_43a_40_41_23];
        case Yrke.Lakare:
          if (formType === TypeOfAnsokan.Specialistkompetens) return [];
          return [Document.Artikel_24_23];
        case Yrke.Sjukskoterska:
          return formType !== TypeOfAnsokan.Specialistkompetens ? [Document.Artikel_31_33_33a_23] : [];
        case Yrke.Tandlakare:
          return formType !== TypeOfAnsokan.Specialistkompetens ? [Document.Artikel_34_37_23] : [];
        default:
          return [Document.Tjanstgoringsintyg];
      }
    }
  };

  private getBaseDocuments = (formType?: TypeOfAnsokan, educatedInSweden = false, yrke?: Yrke) => {
    let baseList = [];
    if (this.isSkyddadYrkestitelForm) {
      baseList = educatedInSweden
        ? [
            Document.KvittensForBetalning,
            Document.PersonbevisEllerPass,
            Document.DokumentFranUtbildning,
            Document.AndraIntyg,
          ]
        : [
            Document.KvittensForBetalning,
            Document.PersonbevisEllerPass,
            Document.DokumentFranUtbildning,
            Document.Kursbeskrivning,
            Document.Sprakintyg,
            Document.IntygAktuellBehorighet,
            Document.IntygNamnbyte,
            Document.AndraIntyg,
          ];
    } else {
      if (formType === TypeOfAnsokan.Legitimation) {
        // Always included for Legitimation
        baseList = [
          Document.KvittensForBetalning,
          Document.PersonbevisEllerPass,
          Document.Sprakintyg,
          Document.Examensbevis,
          Document.IntygNamnbyte,
          Document.IntygAktuellBehorighet,
        ];
        // DokumentFranUtbildning ska inte vara med för sektorsyrkena.
        const sektorsyrken: Yrke[] = [
          Yrke.Apotekare,
          Yrke.Barnmorska,
          Yrke.Lakare,
          Yrke.Sjukskoterska,
          Yrke.Tandlakare,
        ];
        if (!yrke || !sektorsyrken.includes(yrke)) {
          baseList.push(Document.DokumentFranUtbildning);
        }
      }
      if (formType === TypeOfAnsokan.Specialistkompetens) {
        baseList = [Document.KvittensForBetalning, Document.PersonbevisEllerPass, Document.IntygNamnbyte];
      }
    }
    // Makes a clone of DocumentMap, then filters it to only include the documents in baseList
    return new Map(_.cloneDeep([...this.DocumentMap].filter(([key, value]) => baseList.includes(key))));
  };

  private createDocumentMap = () => {
    const map = new Map<Document, DocumentModel>();
    if (this.isSkyddadYrkestitelForm) {
      DOCUMENT_LIST_SKYDDAD_YRKESTITEL.forEach((document, index) => {
        map.set(document, {
          fieldName: document,
          isRequired: false,
          title: this.Translate(document),
          infobubble: this.skyddadYrkestitelOpenFormInfoBubbles['bubble_' + document],
          order: index,
          files: [],
        });
      });
    } else {
      DOCUMENT_LIST.forEach((document, index) => {
        map.set(document, {
          fieldName: document,
          isRequired: false,
          title: this.Translate(document),
          infobubble: this.openWebAnsokanInfoBubbles['bubble_' + document],
          order: index,
          files: [],
        });
      });
    }
    return map;
  };

  private adjustDocumentOrder = (models: Map<Document, DocumentModel>) => {
    if (models.has(Document.IntygAktuellBehorighet)) {
      // Move IntygAktuellBehorighet to the end
      const intygModel = models.get(Document.IntygAktuellBehorighet);
      const maxOrder = Math.max(...Array.from(models.values()).map((m) => m.order));
      intygModel.order = maxOrder + 1;
    }
  };

  private isSpecialistLakareOrTandlakare = (yrke: Yrke, hasSpecialistkompetens: boolean) => {
    return (yrke === Yrke.Lakare || yrke === Yrke.Tandlakare) && hasSpecialistkompetens;
  };
}
